export const CHALLENGE_DAILY_PROFIT_LOCK_MESSAGE =
  'Daily profit limit reached. Trading resumes tomorrow.';

export const RISK_PROFILE_LOCK_MESSAGE =
  'Trading for this Account is Disabled (Risk Lock)';

export const CLOSE_ONLY_MESSAGE =
  'The status of this account is set to Close Only. You can only close your existing Positions.';

export const CHALLENGE_ENDED_MESSAGE =
  'This account is in view-only mode because the challenge has ended. You can review your results, but trading is disabled.';

export type ChallengeOutcome = 'passed' | 'failed' | 'completed' | null;

type ChallengeInfo = {
  isChallenge?: boolean;
  ended?: boolean;
  status?: ChallengeOutcome;
  fail_reason?: string | null;
  phase_id?: number | string | null;
  phase_key?: string | null;
  is_funded?: boolean;
  /** 1-based order among this challenge's phases (ignores plan_phases.id gaps). */
  phase_index?: number | string | null;
  phase_count?: number | string | null;
  daily_profit_locked?: boolean;
  trading_lock?: {
    reason?: string;
    message?: string;
  } | null;
};

type UserDetails = {
  riskLocked?: boolean;
  accessRight?: string | number;
  challengeEnded?: boolean;
  challengeOutcome?: ChallengeOutcome;
  challengeInfo?: ChallengeInfo | null;
};

const toTitleCase = (text: string) =>
  text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Sequential label for this challenge's phases.
 * e.g. challenge with phase_id 1 + 3 → "Phase 1" / "Phase 2 Funded" (last).
 * Do not use raw phase_id (would show "Phase 3" for a 2-step plan).
 */
export function getChallengePhaseLabel(challenge?: ChallengeInfo | null): string {
  if (!challenge) return '';

  const phaseIndex = Number(challenge.phase_index);
  const phaseCount = Number(challenge.phase_count);
  const isLast =
    challenge.is_funded ||
    (Number.isFinite(phaseIndex) &&
      Number.isFinite(phaseCount) &&
      phaseCount > 0 &&
      phaseIndex === phaseCount);

  if (isLast) {
    if (Number.isFinite(phaseIndex) && phaseIndex > 0) {
      return `Phase ${phaseIndex} Funded`;
    }
    return 'Funded';
  }
  if (Number.isFinite(phaseIndex) && phaseIndex > 0) return `Phase ${phaseIndex}`;

  // Fallback: ordinal keys from server (phase_01_funded) or legacy phase_key.
  const rawPhaseKey = String(challenge.phase_key || '').trim();
  if (rawPhaseKey) {
    const ordinalMatch = rawPhaseKey.match(/^phase_0*(\d+)(_funded)?$/i);
    if (ordinalMatch) {
      if (ordinalMatch[2] || challenge.is_funded) {
        return `Phase ${ordinalMatch[1]} Funded`;
      }
      return `Phase ${ordinalMatch[1]}`;
    }
    const normalized = rawPhaseKey.toLowerCase().replace(/_/g, ' ');
    if (normalized.includes('fund')) return 'Funded';
    return toTitleCase(normalized);
  }

  const phaseId = Number(challenge.phase_id);
  if (Number.isFinite(phaseId) && phaseId > 0) return `Phase ${phaseId}`;
  return '';
}


/** Challenge daily profit cap — display only; lock still uses riskLocked on server. */
export function isChallengeDailyProfitLocked(userDetails?: UserDetails | null): boolean {
  if (!userDetails?.challengeInfo) return false;
  const info = userDetails.challengeInfo;
  return !!(
    info.daily_profit_locked ||
    info.trading_lock?.reason === 'daily_profit'
  );
}

/**
 * An ended challenge account is view-only. The server already reports accessRight 3 for it,
 * so this is belt-and-braces for payloads that carry the flag but not the accessRight.
 */
export function isChallengeEnded(userDetails?: UserDetails | null): boolean {
  return !!(userDetails?.challengeEnded || userDetails?.challengeInfo?.ended);
}

/**
 * View-only explanation for an ended challenge. Deliberately never mentions risk lock —
 * the account is frozen by the challenge outcome, not by a risk profile breach.
 */
export function getChallengeEndedMessage(userDetails?: UserDetails | null): string {
  const info = userDetails?.challengeInfo;
  const outcome = userDetails?.challengeOutcome || info?.status || null;
  const phase = getChallengePhaseLabel(info);
  const subject = phase ? `${phase} challenge` : 'Challenge';
  const tail =
    'This account is now in view-only mode. You can review your results, but trading is disabled.';

  if (outcome === 'failed') {
    const reason = String(info?.fail_reason || '').replace(/_/g, ' ').trim();
    return `${subject} failed${reason ? ` (${reason})` : ''}. ${tail}`;
  }
  if (outcome === 'passed') return `${subject} passed. ${tail}`;
  if (outcome === 'completed') return `Challenge completed. ${tail}`;

  return CHALLENGE_ENDED_MESSAGE;
}

/** Unchanged from original UI disable logic, plus ended challenge accounts. */
export function isTradingDisabled(userDetails?: UserDetails | null): boolean {
  if (!userDetails) return false;
  return (
    !!userDetails.riskLocked ||
    userDetails.accessRight == 3 ||
    isChallengeEnded(userDetails)
  );
}

/** Picks banner text only — does not affect whether trading is blocked. */
export function getTradingDisabledBannerMessage(
  userDetails?: UserDetails | null
): string | null {
  if (!userDetails) return null;

  // Before the risk-lock branch: an ended challenge also reports accessRight 3, and
  // "Risk Lock" would be the wrong explanation for it.
  if (isChallengeEnded(userDetails)) {
    return getChallengeEndedMessage(userDetails);
  }

  if (isChallengeDailyProfitLocked(userDetails)) {
    return (
      userDetails.challengeInfo?.trading_lock?.message ||
      CHALLENGE_DAILY_PROFIT_LOCK_MESSAGE
    );
  }

  if (userDetails.riskLocked || userDetails.accessRight == 3) {
    return RISK_PROFILE_LOCK_MESSAGE;
  }

  if (userDetails.accessRight == 2) {
    return CLOSE_ONLY_MESSAGE;
  }

  return null;
}
